import moment from 'moment';
import request from 'api/request';
import {handleError, formValidation, formatter, toFixed} from 'helpers';
import {
  MAX_NUM_SELECT_OPTIONS,
  ItemTypes,
  MAX_NOTE_LEN,
  API_DATETIME_FORMAT,
  Sorting,
} from 'helpers/constants';

import * as alerts from './alertActions';
import reloadInventory from './inventoryActions';
import {getPriceList} from './priceListActions';
import {priceListItemActionTypes as actions} from './actionTypes';

export const resetItem = () => dispatch =>
  dispatch({type: actions.PRICE_LIST_ITEM_RESET});

export const saveNewItem = (item, showError = true) => (dispatch, getState) => {
  const url = '/priceList';
  const itemToInsert = {...item};

  if (
    item.rate > 0 &&
    (item.itemTypeId === ItemTypes.Discount ||
      item.itemTypeId === ItemTypes.DiscountByPercentage)
  ) {
    itemToInsert.rate = item.rate * -1;
  }
  if (item.itemTypeId !== ItemTypes.Group) {
    itemToInsert.defaultCost = itemToInsert.defaultCost || 0;
  }

  return request({
    url,
    method: 'POST',
    body: itemToInsert,
  })
    .then(response => {
      if (response.status === 422) {
        throw new Error(response.data);
      } else if (response.status === 500) {
        throw new Error('An unknown server has occurred');
      }

      reloadInventory(dispatch, getState);

      return response.data;
    })
    .catch(error => {
      if (showError) {
        handleError('Unable to save item', error, dispatch);
      }
      return {error};
    });
};

export const updateItem = item => (dispatch, getState) => {
  const itemToUpdate = {...item};
  if (
    item.rate.value > 0 &&
    (item.itemTypeId.value === ItemTypes.Discount ||
      item.itemTypeId.value === ItemTypes.DiscountByPercentage)
  ) {
    itemToUpdate.rate.value = item.rate.value * -1;
    itemToUpdate.rate.isDirty = true;
  }

  const formObject = formValidation.setupFormBody(itemToUpdate);

  if (formObject.hasError) {
    dispatch(alerts.error(formObject.errorMessage));
    return Promise.reject();
  }

  const url = `/priceList/${item.itemTypeId.value}/${
    item.priceListItemId.value
  }`;

  dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_LOADING});

  return request({
    url,
    method: 'PATCH',
    body: formObject.body,
  })
    .then(response => {
      if (!response) {
        throw new Error('no response');
      }
      if (response.status === 422) {
        throw new Error(response.data);
      }
      dispatch(alerts.success('Item saved'));
      reloadInventory(dispatch, getState);
      return response.data;
    })
    .catch(error => {
      dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_COMPLETE});
      handleError('Unable to save item', error, dispatch);
    });
};

// look up parents for new item
export const changeParentFilter = (
  filterValue,
  itemTypeId,
  excludeItemTypes = '6,7',
) => dispatch => {
  return dispatch(
    getPriceList({
      searchString: filterValue,
      itemTypeId,
      pageInfo: {pageNumber: 1, pageSize: MAX_NUM_SELECT_OPTIONS},
      sortInfo: {sortColumn: 'qualifiedName', sortOrder: Sorting.asc},
      actionType: actions.PRICE_LIST_LOAD_PARENT_ITEMS_SUCCESS,
      excludeItemTypes,
    }),
  );
};

export const loadItem = (itemTypeId, itemId) => dispatch => {
  const url = `/pricelist/${itemTypeId}/${itemId}`;

  dispatch({type: actions.PRICE_LIST_GET_ITEM_LOADING});
  return request({url})
    .then(response => {
      const item = {
        ...response.data,
        quantityAsOfDate:
          response.data.quantityAsOfDate &&
          moment(response.data.quantityAsOfDate, API_DATETIME_FORMAT).format(
            API_DATETIME_FORMAT,
          ),
      }; // truncate the offset because it should be utc

      dispatch({
        type: actions.PRICE_LIST_GET_ITEM_SUCCESS,
        item,
      });
      if (response.data.parentId) {
        request({
          url: `/pricelist/${itemTypeId}/${response.data.parentId}`,
        }).then(parentResp => {
          dispatch({
            type: actions.PRICE_LIST_GET_ITEM_PARENT_SUCCESS,
            parentItem: parentResp.data,
          });
        });
      } else {
        dispatch({
          type: actions.PRICE_LIST_GET_ITEM_PARENT_SUCCESS,
          parentItem: null,
        });
      }
      return item;
    })
    .catch(error => {
      handleError('Unable to load item', error, dispatch);
      dispatch({type: actions.PRICE_LIST_GET_ITEM_FAIL});
    });
};

export const loadItemInventory = itemId => dispatch => {
  dispatch({
    type: actions.PRICE_LIST_ITEM_GET_INVENTORY_LOADING,
    itemId,
  });
  return request({
    url: `/inventoryitems/${itemId}/locations`,
  })
    .then(data => {
      const results =
        data && data.data && data.data.results ? data.data.results : [];
      dispatch({
        type: actions.PRICE_LIST_ITEM_GET_INVENTORY_SUCCESS,
        itemId,
        itemLocations: results,
      });
    })
    .catch(error => {
      handleError('Unable to load Inventory Locations', error, dispatch);
    });
};

export const deleteItem = (item, deleteChildren) => (dispatch, getState) => {
  dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_LOADING});
  let url = `/PriceList/${item.itemTypeId.value}/${item.priceListItemId.value}`;
  url += `?deleteChildren=${deleteChildren}`;
  return request({
    url,
    method: 'DELETE',
  })
    .then(() => {
      dispatch(alerts.success('Successfully deleted'));
      reloadInventory(dispatch, getState);
      dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_COMPLETE});
      return item;
    })
    .catch(error => {
      handleError('Unable to delete item', error, dispatch);
      dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_COMPLETE});
    });
};

export const restoreItem = (itemTypeId, priceListItemId, name) => (
  dispatch,
  getState,
) => {
  dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_LOADING});
  return request({
    url: `/PriceList/${itemTypeId}/${priceListItemId}`,
    body: [
      {
        op: 'replace',
        path: '/isActive',
        value: true,
      },
      {
        op: 'replace',
        path: '/name',
        value: name.replace(' Deleted', ''),
      },
    ],
    method: 'PATCH',
  })
    .then(response => {
      if (!response) {
        throw new Error('no response');
      }
      if (response.status === 422) {
        throw new Error(response.data);
      }
      dispatch(alerts.success('Successfully restored'));
      reloadInventory(dispatch, getState);
      dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_COMPLETE});
      return response.data;
    })
    .catch(error => {
      handleError('Unable to restore item', error, dispatch);
      dispatch({type: actions.PRICE_LIST_ITEM_UPDATE_COMPLETE});
    });
};

export const getGroupItems = priceListItemId => dispatch => {
  dispatch({type: actions.GET_PRICE_LIST_GROUP_ITEMS_LOADING});

  const url = `/pricelist/8/${priceListItemId}/items`;

  return request({url})
    .then(response => {
      if (!response) {
        throw new Error('No response.');
      } else if (response.status === 422) {
        throw new Error(response.data);
      }

      dispatch({type: actions.GET_PRICE_LIST_GROUP_ITEMS_SUCCESS});

      return response.data.map(item => ({
        ...item,
        cost: item.cost || 0,
        rate:
          item.itemTypeId === ItemTypes.Tax ? item.rate * 100 : item.rate || 0,
        amount:
          parseFloat(item.rate || 0) *
          parseFloat(
            item.itemTypeId === ItemTypes.Service
              ? item.quantityDuration / 60
              : item.quantityDuration,
          ),
        displayDescription: formatter.formatDescription(
          item.description,
          MAX_NOTE_LEN,
        ),
        quantityDuration:
          item.itemTypeId === ItemTypes.Service
            ? toFixed(item.quantityDuration / 60, 2)
            : item.quantityDuration,
      }));
    })
    .catch(error => {
      handleError('Unable to get group items', error, dispatch);
    });
};

export const updateGroupItems = (priceListItemId, items) => (
  dispatch,
  getState,
) => {
  const url = `/pricelist/8/${priceListItemId}/items`;
  const body = items.map(x => {
    return {
      quantityDuration:
        x.itemTypeId === ItemTypes.Service
          ? x.quantityDuration * 60
          : x.quantityDuration,
      itemTypeId: x.itemTypeId,
      priceListItemId: x.priceListItemId,
      sortOrder: x.sortOrder,
    };
  });

  return request({
    body,
    method: 'PUT',
    url,
  })
    .then(response => {
      if (!response) {
        throw new Error('no response');
      }

      if (response.status === 422) {
        throw new Error(response.data);
      }

      reloadInventory(dispatch, getState);

      return response.data;
    })
    .catch(error => {
      handleError('Unable to save item', error, dispatch);
    });
};



// WEBPACK FOOTER //
// ./src/actions/priceListItemActions.js